import React, { useState } from 'react';
import {
  Text,
  Button,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import {
  ArrowLeft20Regular,
  ArrowRight20Regular,
  Checkmark20Regular,
} from '@fluentui/react-icons';
import { DomainCard } from './DomainCard';
import { PolicyMultiSelect } from './PolicyMultiSelect';
import { ApprovedDomain, DomainAssignment } from './types';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('24px'),
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('8px'),
  },
  title: {
    fontSize: '20px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground1,
  },
  stepIndicator: {
    fontSize: '14px',
    color: tokens.colorNeutralForeground3,
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('16px'),
  },
  sectionTitle: {
    fontSize: '14px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground1,
    marginTop: '8px',
  },
  helperText: {
    fontSize: '13px',
    color: tokens.colorNeutralForeground3,
    ...shorthands.padding('12px'),
    backgroundColor: tokens.colorNeutralBackground3,
    ...shorthands.borderRadius(tokens.borderRadiusSmall),
  },
  actions: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: '24px',
    ...shorthands.padding('16px', '0', '0', '0'),
    ...shorthands.borderTop('1px', 'solid', tokens.colorNeutralStroke2),
  },
  leftActions: {
    display: 'flex',
    ...shorthands.gap('12px'),
  },
  rightActions: {
    display: 'flex',
    ...shorthands.gap('12px'),
  },
});

interface StepByStepAssignmentProps {
  domains: ApprovedDomain[];
  onBack: () => void;
  onComplete: (assignments: DomainAssignment[]) => void;
}

export const StepByStepAssignment: React.FC<StepByStepAssignmentProps> = ({
  domains,
  onBack,
  onComplete,
}) => {
  const styles = useStyles();
  const [currentStep, setCurrentStep] = useState(0);
  const [assignments, setAssignments] = useState<Map<string, string[]>>(new Map());

  const currentDomain = domains[currentStep];
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === domains.length - 1;

  const currentPolicies = assignments.get(currentDomain.id) || [];

  const handlePolicyChange = (policyIds: string[]) => {
    const newAssignments = new Map(assignments);
    newAssignments.set(currentDomain.id, policyIds);
    setAssignments(newAssignments);
  };

  const handleSkip = () => {
    const newAssignments = new Map(assignments);
    newAssignments.set(currentDomain.id, []); // Empty array means skipped
    setAssignments(newAssignments);
    handleNext();
  };

  const handleNext = () => {
    if (isLastStep) {
      // Convert to DomainAssignment format
      const finalAssignments: DomainAssignment[] = domains.map(domain => ({
        domainId: domain.id,
        domainName: domain.domain,
        policyIds: assignments.get(domain.id) || [],
        status: (assignments.get(domain.id) || []).length > 0 ? 'pending' : 'skipped',
      }));
      onComplete(finalAssignments);
    } else {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (!isFirstStep) {
      setCurrentStep(currentStep - 1);
    }
  };

  return (
    <div className={styles.container} data-testid="step-by-step-assignment">
      <div className={styles.helperText} data-testid="step-by-step-assignment-helper-text-container">
        <Text data-testid="step-by-step-assignment-helper-text">
          This domain will be added to the allowed domains list of the selected policies. Skip it if you don’t want to assign it right now.
        </Text>
      </div>
      
      <div className={styles.header} data-testid="step-by-step-assignment-header">
        <Text className={styles.stepIndicator} data-testid="step-by-step-assignment-step-indicator">
          Assigning domain {currentStep + 1} of {domains.length}
        </Text>
      </div>

      <div className={styles.content} data-testid="step-by-step-assignment-content">
        <DomainCard domain={currentDomain} />

        <div data-testid="step-by-step-assignment-policy-section">
          <Text className={styles.sectionTitle} data-testid="step-by-step-assignment-policy-section-title">
            Select policies for this domain:
          </Text>
          <div style={{ marginTop: '12px' }} data-testid="step-by-step-assignment-policy-selector">
            <PolicyMultiSelect
              selectedPolicyIds={currentPolicies}
              onSelectionChange={handlePolicyChange}
              placeholder="Select one or more policies..."
              helperText={
                currentPolicies.length > 0
                  ? `This domain will be added to ${currentPolicies.length} ${
                      currentPolicies.length === 1 ? 'policy' : 'policies'
                    }`
                  : 'Select policies or skip to continue'
              }
            />
          </div>
        </div>
      </div>

      <div className={styles.actions} data-testid="step-by-step-assignment-actions">
        <div className={styles.leftActions} data-testid="step-by-step-assignment-left-actions">
          {isFirstStep ? (
            <Button
              appearance="subtle"
              onClick={onBack}
              icon={<ArrowLeft20Regular data-testid="step-by-step-back-to-mode-icon" />}
              data-testid="step-by-step-back-to-mode"
            >
              Back to Mode Selection
            </Button>
          ) : (
            <Button
              appearance="subtle"
              onClick={handlePrevious}
              icon={<ArrowLeft20Regular data-testid="step-by-step-previous-icon" />}
              data-testid="step-by-step-previous"
            >
              Previous
            </Button>
          )}
        </div>

        <div className={styles.rightActions} data-testid="step-by-step-assignment-right-actions">
          <Button appearance="subtle" onClick={handleSkip} data-testid="step-by-step-skip-domain">
            Skip This Domain
          </Button>
          <Button
            appearance="primary"
            onClick={handleNext}
            icon={isLastStep ? <Checkmark20Regular data-testid="step-by-step-next-domain-complete-icon" /> : <ArrowRight20Regular data-testid="step-by-step-next-domain-arrow-icon" />}
            iconPosition="after"
            data-testid="step-by-step-next-domain"
          >
            {isLastStep ? 'Review & Apply' : 'Next Domain'}
          </Button>
        </div>
      </div>
    </div>
  );
};
